import React, { useRef, useEffect, useState } from "react";
import { Box, Theme, useMediaQuery } from "@mui/material";
import { DirectionControllerService } from "../../services";

interface Section {
  label: string;
  startAngle: number;
  endAngle: number;
  subSections: string[];
}

interface CompassProps {
  rotation: number;
}

const Compass: React.FC<CompassProps> = ({ rotation }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const [hoveredSection, setHoveredSection] = useState<string | null>(null);
  const [hoveredSubSection, setHoveredSubSection] = useState<string | null>(
    null
  );

  const [sections, setSections] = useState<Section[]>([]);

  const isXs = useMediaQuery((theme: Theme) => theme.breakpoints.down("xs"));
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const isMd = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const isLg = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));

  const canvasSize = isXs ? 200 : isSm ? 300 : isMd ? 400 : isLg ? 450 : 700;
  // Make the radius smaller to leave room for labels
  const radius = canvasSize / 2.5;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response =
          await DirectionControllerService.directionControllerGetAllDirections();

        const fetchedSections = response
          .filter((item: any) => item.displayName !== "Bramhasthan")
          .map((item: any) => ({
            label: item.displayName,
            startAngle: item.start,
            endAngle: item.end,
            subSections: item.entrances || [],
          }));


        setSections(fetchedSections);
      } catch (error) {
        console.error("Error fetching compass data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;

    const baseAngleOffset = -90;
    const angleOffset = baseAngleOffset + rotation;

    ctx.clearRect(0, 0, width, height);

    ctx.beginPath();
    ctx.arc(width / 2, height / 2, radius - 20, 0, Math.PI * 2);
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = "grey";
    ctx.stroke();

    for (let i = 0; i < 360; i += 5) {
      const angleRad = ((i + angleOffset) * Math.PI) / 180;
      const x1 = width / 2 + (radius - 10) * Math.cos(angleRad);
      const y1 = height / 2 + (radius - 10) * Math.sin(angleRad);
      const x2 = width / 2 + radius * Math.cos(angleRad);
      const y2 = height / 2 + radius * Math.sin(angleRad);

      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);

      ctx.lineWidth = i % 10 === 0 ? 1 : 0.5;
      ctx.stroke();

      if (i % 30 === 0) {
        const labelX = width / 2 + (radius - 10) * Math.cos(angleRad);
        const labelY = height / 2 + (radius - 10) * Math.sin(angleRad);
        ctx.font = "12px Arial";
        ctx.fillStyle = "black";
        ctx.textAlign = "center";
        ctx.fillText(`${i}°`, labelX, labelY);
      }
    }

    ctx.beginPath();
    ctx.arc(width / 2, height / 2, 5, 0, Math.PI * 2);
    ctx.fillStyle = "#FF0000";
    ctx.fill();

    sections.forEach(({ label, startAngle, endAngle, subSections }) => {
      const startRad = ((startAngle + angleOffset) * Math.PI) / 180;
      const endRad = ((endAngle + angleOffset) * Math.PI) / 180;
      const totalSectionDegrees =
        endAngle >= startAngle
          ? endAngle - startAngle
          : endAngle + 360 - startAngle;

      const midAngle = (startAngle + totalSectionDegrees / 2) % 360;
      const midRad = ((midAngle + angleOffset) * Math.PI) / 180;

      ctx.beginPath();
      ctx.moveTo(width / 2, height / 2);
      ctx.arc(width / 2, height / 2, radius, startRad, endRad);
      ctx.lineTo(width / 2, height / 2);

      ctx.fillStyle =
        hoveredSection === label && !hoveredSubSection
          ? "rgba(173, 216, 230, 0.3)"
          : "transparent";
      ctx.fill();
      ctx.lineWidth = 1;
      ctx.stroke();

      const labelX = width / 2 + (radius + 40) * Math.cos(midRad);
      const labelY = height / 2 + (radius + 40) * Math.sin(midRad);
      ctx.font = "16px Arial";
      ctx.fillStyle = "black";
      ctx.textAlign = "center";
      ctx.fillText(label, labelX, labelY);
      const subWedgeDeg = totalSectionDegrees / subSections.length;

      subSections.forEach((subSec, idx) => {
        const subStart = startAngle + idx * subWedgeDeg;
        const subEnd = subStart + subWedgeDeg;

        const subStartRad = ((subStart + angleOffset) * Math.PI) / 180;
        const subEndRad = ((subEnd + angleOffset) * Math.PI) / 180;
        const subMidRad = (subStartRad + subEndRad) / 2;

        ctx.beginPath();
        ctx.moveTo(width / 2, height / 2);

        ctx.arc(width / 2, height / 2, radius - 50, subStartRad, subEndRad);
        ctx.lineTo(width / 2, height / 2);

        ctx.fillStyle =
          hoveredSubSection === subSec
            ? "rgba(144, 238, 144, 0.3)"
            : "transparent";
        ctx.fill();
        ctx.lineWidth = 0.5;
        ctx.stroke();

        const subLabelX = width / 2 + (radius - 70) * Math.cos(subMidRad);
        const subLabelY = height / 2 + (radius - 70) * Math.sin(subMidRad);
        ctx.font = "12px Arial";
        ctx.fillStyle = "black";
        ctx.textAlign = "center";
        ctx.fillText(subSec, subLabelX, subLabelY);
      });
    });
  }, [sections, hoveredSection, hoveredSubSection, radius, rotation]);

  const baseAngleOffset = -90;
  const angleOffset = baseAngleOffset + rotation;

  const getSectionByAngle = (deg: number): string | null => {
    return (
      sections.find(({ startAngle, endAngle }) => {
        if (startAngle <= endAngle) {
          return deg >= startAngle && deg <= endAngle;
        } else {
          return deg >= startAngle || deg <= endAngle;
        }
      })?.label || null
    );
  };

  const getSubSectionByAngle = (
    deg: number,
    mainLabel: string
  ): string | null => {
    const section = sections.find((sec) => sec.label === mainLabel);
    if (!section) return null;

    const { startAngle, endAngle, subSections } = section;
    const totalSectionDeg =
      endAngle >= startAngle
        ? endAngle - startAngle
        : endAngle + 360 - startAngle;
    const subWedgeDeg = totalSectionDeg / subSections.length;

    for (let i = 0; i < subSections.length; i++) {
      const subStart = startAngle + i * subWedgeDeg;
      const subEnd = subStart + subWedgeDeg;

      if (subStart <= subEnd) {
        if (deg >= subStart && deg <= subEnd) return subSections[i];
      } else {
        if (deg >= subStart || deg <= subEnd) return subSections[i];
      }
    }

    return null;
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const mouseX = (e.clientX - rect.left) * scaleX;
    const mouseY = (e.clientY - rect.top) * scaleY;

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    let angleDeg =
      (Math.atan2(mouseY - centerY, mouseX - centerX) * 180) / Math.PI;
    angleDeg = (angleDeg + 360) % 360;

    const adjustedDeg = (angleDeg - angleOffset + 360) % 360;

    const distFromCenter = Math.sqrt(
      (mouseX - centerX) ** 2 + (mouseY - centerY) ** 2
    );

    if (distFromCenter <= radius) {
      const mainLabel = getSectionByAngle(adjustedDeg);
      if (mainLabel) {
        if (distFromCenter <= radius - 50) {
          const subLabel = getSubSectionByAngle(adjustedDeg, mainLabel);
          setHoveredSubSection(subLabel);
        } else {
          setHoveredSubSection(null);
        }
        setHoveredSection(mainLabel);
      } else {
        setHoveredSection(null);
        setHoveredSubSection(null);
      }
    } else {
      setHoveredSection(null);
      setHoveredSubSection(null);
    }
  };

  return (
    <Box
      sx={{
        width: canvasSize,
        height: canvasSize,
        overflow: "hidden",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <canvas
        ref={canvasRef}
        width={canvasSize}
        height={canvasSize}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => {
          setHoveredSection(null);
          setHoveredSubSection(null);
        }}
        style={{
          border: "1px solid #ccc",
        }}
      />
    </Box>
  );
};

export default Compass;
